import React from 'react';
import { DialogV2 } from '@withjoy/joykit';
import { AmountStep } from './AmountStep';
import { MethodStep } from './MethodStep';
import { ConnectBankStep } from './ConnectBankStep';
import { StatusPayoutDialog } from './TransferStatus';
import type { PayoutAccountInfo, PayoutDialogStep, PayoutStatus, PayoutMethod } from './PayoutDialog.types';

export type { PayoutStatus, PayoutDialogStep };

interface PayoutDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly payoutAccountInfo: PayoutAccountInfo;
  readonly dialogStep: PayoutDialogStep;
  readonly amountInMinorUnits: number;
  readonly onAmountChange: (amount: number) => void;
  readonly onContinue: () => void;
  readonly onBack: () => void;
  readonly selectedMethod: PayoutMethod;
  readonly onMethodChange: (method: PayoutMethod) => void;
  readonly onTransfer: () => void;
  readonly loadingTransfer: boolean;
  readonly status: PayoutStatus;
  readonly onConnectBank?: () => void;
  readonly onAmountStepViewed?: () => void;
  readonly onMethodStepViewed?: () => void;
  readonly onStatusDone?: (status: PayoutStatus) => void;
  readonly onBankPromoViewed?: (partner: string) => void;
  readonly onBankPromoInteracted?: (cta: string, partner: string) => void;
}

export const PayoutDialog: React.FC<PayoutDialogProps> = ({
  onClose,
  isOpen,
  payoutAccountInfo,
  dialogStep,
  amountInMinorUnits,
  onAmountChange,
  onContinue,
  onBack,
  selectedMethod,
  onMethodChange,
  onTransfer,
  loadingTransfer,
  status,
  onConnectBank,
  onAmountStepViewed,
  onMethodStepViewed,
  onStatusDone,
  onBankPromoViewed,
  onBankPromoInteracted,
}) => {
  return (
    <DialogV2
      onClose={onClose}
      isOpen={isOpen}
      size="lg"
    >
      <DialogV2.CloseButton />
      <DialogV2.Body
        marginX={7}
        marginY={8}
      >
        {dialogStep === 'connectBank' && onConnectBank && (
          <ConnectBankStep
            payoutAccountInfo={payoutAccountInfo}
            onConnectBank={onConnectBank}
            onBankPromoViewed={onBankPromoViewed}
            onBankPromoInteracted={onBankPromoInteracted}
          />
        )}
        {dialogStep === 'amount' && (
          <AmountStep
            payoutAccountInfo={payoutAccountInfo}
            amountInMinorUnits={amountInMinorUnits}
            onAmountChange={onAmountChange}
            onContinue={onContinue}
            onPageViewed={onAmountStepViewed}
          />
        )}
        {dialogStep === 'method' && (
          <MethodStep
            payoutAccountInfo={payoutAccountInfo}
            amountInMinorUnits={amountInMinorUnits}
            selectedMethod={selectedMethod}
            onMethodChange={onMethodChange}
            onTransfer={onTransfer}
            loadingTransfer={loadingTransfer}
            onBack={onBack}
            onPageViewed={onMethodStepViewed}
          />
        )}
        {(dialogStep === 'confirmation' || status === 'error') && (
          <StatusPayoutDialog
            onClose={onClose}
            status={status}
            onDone={onStatusDone}
          />
        )}
      </DialogV2.Body>
    </DialogV2>
  );
};
